import {
  Country,
  countryCategory,
  tournamentCountriesInfo,
  type Country as CountryType,
} from '@/models/country';
import type { Fixture } from '@/models/fixture';
import type { gameResult } from '@/models/gameUpdate';
import type { ProgressionAward } from '@/models/scoring';
import { sortFixturesChronological, sortFixturesRecentFirst } from '@/helpers';

export type TeamDetailSource =
  | 'profile-list'
  | 'fixtures'
  | 'teams-page'
  | 'edit-list'
  | 'league'
  | 'rules-demo'
  | 'map';

export type TeamDetailOptions = {
  rank?: number;
  source?: TeamDetailSource;
  /** Invoked when team detail closes (e.g. reopen a parent modal dismissed for scroll stacking). */
  onCloseResume?: () => void;
};

export function isClickableTeamCountry(country: CountryType): boolean {
  return country !== Country.TBD;
}

export function getTeamCategoryLabel(country: CountryType): string {
  const cat = tournamentCountriesInfo[country]?.category;
  switch (cat) {
    case countryCategory.Favourite:
      return 'Seed';
    case countryCategory.Intermediate:
      return 'Intermediate';
    case countryCategory.Outsider:
      return 'Outsider';
    default:
      return 'Unknown';
  }
}

export function getTeamCategoryColorScheme(
  country: CountryType,
): 'purple' | 'blue' | 'orange' | 'gray' {
  const cat = tournamentCountriesInfo[country]?.category;
  switch (cat) {
    case countryCategory.Favourite:
      return 'purple';
    case countryCategory.Intermediate:
      return 'blue';
    case countryCategory.Outsider:
      return 'orange';
    default:
      return 'gray';
  }
}

export function filterFixturesForTeam(fixtures: Fixture[], country: CountryType): Fixture[] {
  return fixtures
    .filter((f) => f.homeTeam === country || f.awayTeam === country)
    .sort(sortFixturesChronological);
}

export function filterGameResultsForTeam(gameResults: gameResult[], country: CountryType): gameResult[] {
  return gameResults.filter((r) => r.countryA === country || r.countryB === country);
}

/** Sum match points only (gameUpdates rows — excludes out-of-groups awards). */
export function sumPointsForTeam(gameResults: gameResult[], country: CountryType): number {
  return gameResults.reduce(
    (sum, r) => sum + (r.countryA === country ? r.pointsA : r.countryB === country ? r.pointsB : 0),
    0,
  );
}

export function sumProgressionPointsForTeam(
  progressionAwards: ProgressionAward[],
  country: CountryType,
): number {
  return progressionAwards.reduce(
    (sum, award) => sum + (award.team === country ? award.points : 0),
    0,
  );
}

/** Tournament total for display — matches buildMPM inputs (match results + progression awards). */
export function sumTournamentPointsForTeam(
  gameResults: gameResult[],
  progressionAwards: ProgressionAward[],
  country: CountryType,
): number {
  return sumPointsForTeam(gameResults, country) + sumProgressionPointsForTeam(progressionAwards, country);
}

export function resolveFixtureMatchId(fixture: Pick<Fixture, 'matchId' | 'sortOrder'>): string {
  return fixture.matchId ?? `m${fixture.sortOrder ?? 0}`;
}

export function findGameResultForFixture(fixture: Fixture, gameResults: gameResult[]): gameResult | undefined {
  const matchId = resolveFixtureMatchId(fixture);
  const byMatchId = gameResults.find((r) => r.matchId === matchId);
  if (byMatchId) return byMatchId;

  const pairKey = `${fixture.homeTeam}:${fixture.awayTeam}`;
  const reverseKey = `${fixture.awayTeam}:${fixture.homeTeam}`;
  return gameResults.find(
    (r) => `${r.countryA}:${r.countryB}` === pairKey || `${r.countryA}:${r.countryB}` === reverseKey,
  );
}

export function getPlayedResultKeysForTeam(
  gameResults: gameResult[],
  country: CountryType,
): Set<string> {
  const keys = new Set<string>();
  for (const r of gameResults) {
    if (r.countryA === country || r.countryB === country) {
      keys.add(`${r.countryA}:${r.countryB}`);
    }
  }
  return keys;
}

export function partitionTeamFixtures(
  fixtures: Fixture[],
  gameResults: gameResult[],
  country: CountryType,
): { upcoming: Fixture[]; played: Fixture[] } {
  const teamFixtures = filterFixturesForTeam(fixtures, country);
  const playedPairs = getPlayedResultKeysForTeam(gameResults, country);
  const now = Date.now();
  const upcoming: Fixture[] = [];
  const played: Fixture[] = [];

  for (const f of teamFixtures) {
    const pairKey = `${f.homeTeam}:${f.awayTeam}`;
    const reverseKey = `${f.awayTeam}:${f.homeTeam}`;
    const hasResult = playedPairs.has(pairKey) || playedPairs.has(reverseKey);
    if (hasResult) {
      played.push(f);
    } else if (f.date.toMillis() > now) {
      upcoming.push(f);
    } else {
      played.push(f);
    }
  }

  // Upcoming: earliest first. Played / past: most recent first.
  upcoming.sort(sortFixturesChronological);
  played.sort(sortFixturesRecentFirst);

  return { upcoming, played };
}

export function getRankOnUserList(country: CountryType, countryList: Country[] | undefined): number | undefined {
  if (!countryList?.length) return undefined;
  const idx = countryList.indexOf(country);
  return idx >= 0 ? idx + 1 : undefined;
}

export type MapCountryTeamSnapshot = {
  points: number;
  nextFixture: Fixture | undefined;
  previousFixture: Fixture | undefined;
  previousResult: gameResult | undefined;
};

/** Abridged team data for the map country overlay card. */
export function getMapCountryTeamSnapshot(
  country: CountryType,
  fixtures: Fixture[],
  gameResults: gameResult[],
  progressionAwards: ProgressionAward[] = [],
): MapCountryTeamSnapshot {
  const { upcoming, played } = partitionTeamFixtures(fixtures, gameResults, country);
  const previousFixture = played.length > 0 ? played[0] : undefined;
  const previousResult = previousFixture
    ? findGameResultForFixture(previousFixture, gameResults)
    : undefined;

  return {
    points: sumTournamentPointsForTeam(gameResults, progressionAwards, country),
    nextFixture: upcoming[0],
    previousFixture,
    previousResult,
  };
}
